// smapro-insights/src/LoginSMAv2.tsx
import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Menu, 
  MenuItem, 
  Alert, 
  CircularProgress, 
  Stack,
  Divider,
  Typography,
  useTheme,
  alpha,
  Paper
} from '@mui/material';
import { 
  Google as GoogleIcon,
  Twitter as TwitterIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { User } from '../../types/auth';

interface LoginSMAv2Props {
  showRegistration?: boolean;
  showSubscriptionManager?: boolean;
  onLoginSuccess?: (userData: User) => void;
  loginPath: string;
  setError: (error: string | null) => void;
  urlBase: string;
}

export const LoginSMAv2: React.FC<LoginSMAv2Props> = ({
  showRegistration = false,
  showSubscriptionManager = false,
  onLoginSuccess, // This prop might become redundant if success is handled globally after redirect
  loginPath, // e.g., 'register' or 'login'
  setError,
  urlBase
}) => {
  const { isAuthenticated, loading, error: authError, login, user } = useAuth();
  const theme = useTheme();

  // If already authenticated, don't show the login component
  // This check might run briefly before the redirect happens
  if (isAuthenticated && user) {
    // This might still be useful if the component re-renders after successful BFF login
    if (onLoginSuccess) {
      onLoginSuccess(user);
    }
    return null;
  }

  // Direct login handlers for each provider using BFF
  const handleGoogleLogin = async () => {
    try {
      // Pass context to login function
      login('google', true, { authAction: loginPath }); 
      // Code below this line will likely not execute due to redirect
    } catch (err: any) {
      // Catch errors *before* the redirect (e.g., network issue fetching CSRF)
      console.error('Google login initiation error:', err);
      setError(err.message || 'Login initiation failed. Please try again.');
    }
  };

  const handleTwitterLogin = async () => {
    try {
      // Pass context to login function
      login('twitter', true, { authAction: loginPath });
      // Code below this line will likely not execute due to redirect
    } catch (err: any) {
      // Catch errors *before* the redirect (e.g., network issue fetching CSRF)
      console.error('Twitter login initiation error:', err);
      setError(err.message || 'Login initiation failed. Please try again.');
    }
  };

  return (
    <Box>
      {/* Display authError from AuthContext which might be set after redirect */}
      {authError && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 4,
            maxWidth: 400,
            mx: 'auto'
          }} 
          onClose={() => setError(null)} // Consider if setError prop is still needed or if context error is sufficient
        >
          {authError}
        </Alert>
      )}
      
      <Stack 
        spacing={3} 
        sx={{ 
          maxWidth: 400, 
          mx: 'auto'
        }}
      >
        <Button
          variant="contained"
          fullWidth
          size="large"
          disabled={loading} // loading state from AuthContext indicates process ongoing
          startIcon={<GoogleIcon />}
          onClick={handleGoogleLogin}
          sx={{ 
            py: 1.5,
            bgcolor: '#4285F4',
            color: '#fff',
            boxShadow: '0 2px 6px rgba(66, 133, 244, 0.4)',
            '&:hover': {
              bgcolor: '#3367D6',
              boxShadow: '0 4px 12px rgba(66, 133, 244, 0.5)',
            }
          }}
        >
          {/* loading state will be true during the brief period before redirect */}
          {loading ? 'Redirecting...' : 'Continue with Google'}
        </Button>
        
        {/* <Button
          variant="contained"
          fullWidth
          size="large"
          disabled={loading} // loading state from AuthContext indicates process ongoing
          startIcon={<TwitterIcon />}
          onClick={handleTwitterLogin}
          sx={{ 
            py: 1.5,
            bgcolor: '#1DA1F2',
            color: '#fff',
            boxShadow: '0 2px 6px rgba(29, 161, 242, 0.4)',
            '&:hover': {
              bgcolor: '#0c85d0',
              boxShadow: '0 4px 12px rgba(29, 161, 242, 0.5)',
            }
          }}
        >
          {loading ? 'Redirecting...' : 'Continue with Twitter'}
        </Button> */}
        
        {/* Loading indicator might show briefly before redirect */}
        {loading && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Preparing secure login...
            </Typography>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
          <Divider sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mx: 2 }}>
            Secure Authentication
          </Typography>
          <Divider sx={{ flexGrow: 1 }} />
        </Box>
        
        <Typography variant="caption" color="text.secondary" align="center">
          By signing in, you agree to our Terms of Service and Privacy Policy.
          We use secure OAuth for authentication and never store your passwords.
        </Typography>
      </Stack>
    </Box>
  );
};

export default LoginSMAv2;